import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateInvitations } from "@/hooks";
import { UtilsService } from "@/services/utils/utils.service";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StyledInput } from "@/components/ui/styled-input";
import { StyledLabel } from "@/components/ui/styled-label";
import { X } from "lucide-react";

const utils = new UtilsService();

interface SendInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SendInvitationModal: React.FC<SendInvitationModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const createInvitationsMutation = useCreateInvitations();
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");

  const addEmail = () => {
    const email = emailInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !inviteEmails.includes(email)) {
      setInviteEmails([...inviteEmails, email]);
      setEmailInput("");
    } else if (inviteEmails.includes(email)) {
      toast.error(t("company.invitations.errors.emailAlreadyAdded"));
    } else {
      toast.error(t("company.invitations.errors.invalidEmail"));
    }
  };

  const removeEmail = (email: string) => {
    setInviteEmails(inviteEmails.filter((e) => e !== email));
  };

  const handleSubmit = async () => {
    if (inviteEmails.length === 0) {
      toast.error(t("company.invitations.errors.noEmails"));
      return;
    }

    try {
      await createInvitationsMutation.mutateAsync({ emails: inviteEmails });
      toast.success(t("onboarding.invitationsSent"));
      setInviteEmails([]);
      setEmailInput("");
      onClose();
    } catch (error: unknown) {
      const errorMessage = utils.extractErrorMessage(
        error,
        t("onboarding.invitationSendFailed")
      );
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setInviteEmails([]);
    setEmailInput("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("company.invitations.sendNew")}</DialogTitle>
          <DialogDescription>
            {t("company.invitations.sendDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <StyledLabel htmlFor="email">
              {t("onboarding.steps.company.emailPlaceholder")}
            </StyledLabel>
            <div className="flex gap-2 mt-1">
              <StyledInput
                id="email"
                type="email"
                placeholder={t("onboarding.steps.company.emailPlaceholder")}
                containerStyles="!rounded flex-1"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail();
                  }
                }}
              />
              <Button type="button" onClick={addEmail} variant="outline">
                {t("onboarding.steps.company.addEmail")}
              </Button>
            </div>
          </div>

          {inviteEmails.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("company.invitations.emailsToInvite")}</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {inviteEmails.map((email) => (
                  <div
                    key={email}
                    className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded"
                  >
                    <span className="text-sm">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={createInvitationsMutation.isPending || inviteEmails.length === 0}
            >
              {createInvitationsMutation.isPending
                ? t("common.saving")
                : t("company.invitations.send")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendInvitationModal;

